import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { auth } from '@/lib/api';

interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
  role: string;
  agency_id: string;
}

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  
  setAuth: (token: string, refreshToken: string, user: User) => void;
  refreshAuth: () => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => void;
}

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as { exp?: number };
    if (!payload?.exp) return false;
    const now = Math.floor(Date.now() / 1000);
    return payload.exp <= now + 30; // refresh if expiring within 30 seconds
  } catch {
    return true;
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      user: null,
      isLoading: true,
      isAuthenticated: false,

      setAuth: (token, refreshToken, user) => {
        set({
          token,
          refreshToken,
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      refreshAuth: async () => {
        const { refreshToken } = get();
        if (!refreshToken) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }

        try {
          const { token } = await auth.refresh(refreshToken);
          set({ token, isAuthenticated: true, isLoading: false });
        } catch {
          // Refresh failed, clear auth
          set({
            token: null,
            refreshToken: null,
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      logout: async () => {
        const { refreshToken } = get();
        if (refreshToken) {
          try {
            await auth.logout(refreshToken);
          } catch {
            // Ignore logout errors
          }
        }
        set({
          token: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      checkAuth: () => {
        const { token, refreshToken, user } = get();
        if (token && user) {
          if (isTokenExpired(token)) {
            get().refreshAuth();
          } else {
            set({ isAuthenticated: true, isLoading: false });
          }
        } else if (refreshToken) {
          get().refreshAuth();
        } else {
          set({ isAuthenticated: false, isLoading: false });
        }
      },
    }),
    {
      name: 'prima-auth',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        state?.checkAuth();
      },
    }
  )
);
